import { useMailRuntime } from "./MailRuntime";
import React, { createContext, useContext, useMemo, useRef, useState } from "react";
import { normalizeDesignPayload } from "../utils/designPayload";
import { DRAFT_PREFIX } from "../config/presets";

const ComposerContext = createContext(null);

export function ComposerProvider({ children, owner }) {
  const runtime = useMailRuntime();
  const [ready, setReady] = useState(false);
  const [templateVersion, setTemplateVersion] = useState(runtime.fixedTemplate?.version || null);
  const [version, setVersion] = useState(null);
  const [notice, setNotice] = useState('');
  const [loadError, setLoadError] = useState('');
  const operationLock = useRef(false);
  const [newsletterId, setNewsletterId] = useState(null);
  const [subject, setSubject] = useState("");
  const [recipients, setRecipients] = useState("");
  const [selectedList, setSelectedList] = useState("");

  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [presetKey, setPresetKey] = useState("bookish");
  const [outerBg, setOuterBg] = useState("#F6EEDD");
  const [cardBg, setCardBg] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#2c1b47");
  const [accentColor, setAccentColor] = useState("#3F256B");
  const [headerBg, setHeaderBg] = useState("#541675");
  const [headerTextColor, setHeaderTextColor] = useState("#ffffff");
  const [headerButtonColor, setHeaderButtonColor] = useState("#ffffff");
  const [headerButtonText, setHeaderButtonText] = useState("#000000");
  const [fontFamily, setFontFamily] = useState("Georgia, serif");
  const [fontSize, setFontSize] = useState("16px");
  const [contentWidth, setContentWidth] = useState("700px");

  const [composerMode, setComposerMode] = useState("design");
  const [preheaderText, setPreheaderText] = useState("");
  const [footer, setFooter] = useState({ enabled: true, html: "", socialLinks: {} });

  const [previewMode, setPreviewMode] = useState("desktop");
  const [activeSection, setActiveSection] = useState(null);
  const [sectionMenuPos, setSectionMenuPos] = useState({ top: 0, left: 0 });
  const [imagePopover, setImagePopover] = useState({
    open: false,
    top: 0,
    left: 0,
    link: "",
    size: "medium",
    mode: "gallery",
    isPlaceholder: false,
  });
  const [buttonPopover, setButtonPopover] = useState({
    open: false,
    top: 0,
    left: 0,
    text: "",
    url: "",
  });
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [newTemplateName, setNewTemplateName] = useState("");

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const sortableRef = useRef(null);
  const sectionMenuRef = useRef(null);
  const imagePopoverRef = useRef(null);
  const buttonPopoverRef = useRef(null);


  const getDesignFallback = () => ({
    presetKey,
    outerBg,
    cardBg,
    textColor,
    accentColor,
    headerBg,
    headerTextColor,
    headerButtonColor,
    headerButtonText,
    fontFamily,
    fontSize,
    contentWidth,
  });

  const applyDesignState = (rawDesign = {}) => {
    const design = normalizeDesignPayload(rawDesign, getDesignFallback());
    setOuterBg(design.outerBg);
    setCardBg(design.cardBg);
    setTextColor(design.textColor);
    setAccentColor(design.accentColor);
    setHeaderBg(design.headerBg);
    setHeaderTextColor(design.headerTextColor);
    setHeaderButtonColor(design.headerButtonColor);
    setHeaderButtonText(design.headerButtonText);
    setFontFamily(design.fontFamily);
    setFontSize(design.fontSize);
    setContentWidth(design.contentWidth);
    setPresetKey(design.presetKey);
    return design;
  };

  const draftKey = useMemo(
    () => `${DRAFT_PREFIX}${runtime.storageScope}-${owner}-${newsletterId || "new"}`,
    [newsletterId, runtime.storageScope, owner]
  );

  const value = {
    templateVersion, setTemplateVersion,
    owner, ready, setReady, version, setVersion, notice, setNotice, loadError, setLoadError, operationLock,
    newsletterId, setNewsletterId,
    subject, setSubject,
    recipients, setRecipients,
    selectedList, setSelectedList,
    sending, setSending,
    uploading, setUploading,
    loading, setLoading,
    saving, setSaving,
    presetKey, setPresetKey,
    outerBg, setOuterBg,
    cardBg, setCardBg,
    textColor, setTextColor,
    accentColor, setAccentColor,
    headerBg, setHeaderBg,
    headerTextColor, setHeaderTextColor,
    headerButtonColor, setHeaderButtonColor,
    headerButtonText, setHeaderButtonText,
    fontFamily, setFontFamily,
    fontSize, setFontSize,
    contentWidth, setContentWidth,
    composerMode, setComposerMode,
    preheaderText, setPreheaderText,
    footer, setFooter,
    previewMode, setPreviewMode,
    activeSection, setActiveSection,
    sectionMenuPos, setSectionMenuPos,
    imagePopover, setImagePopover,
    buttonPopover, setButtonPopover,
    templates, setTemplates,
    drafts, setDrafts,
    selectedTemplateId, setSelectedTemplateId,
    newTemplateName, setNewTemplateName,
    editorRef,
    fileInputRef,
    sortableRef,
    sectionMenuRef,
    imagePopoverRef,
    buttonPopoverRef,
    draftKey,
    getDesignFallback,
    applyDesignState,
  };

  return (
    <ComposerContext.Provider value={value}>
      {children}
    </ComposerContext.Provider>
  );
}

export function useComposerContext() {
  const ctx = useContext(ComposerContext);
  if (!ctx) throw new Error("useComposerContext must be used within ComposerProvider");
  return ctx;
}
